/**
 * Shading diagnostic system — debounced logger, sanity checks, snapshot.
 *
 * Master gate: tied to debugEnabled in the store. When debug mode is off,
 * every call is a no-op (no string formatting, no allocation, no timer).
 *
 * Usage from console:
 *   window.__shadingDiag.snapshot()    — capture pipeline state as JSON
 *   window.__shadingDiag.flush()       — force-emit pending deduped entries
 *   window.__shadingDiag.enable("NORMAL")  — enable a category
 *   window.__shadingDiag.getRecent()   — get recent entries
 */

import type * as THREE from "three/webgpu";
import type { ShadingDebugFlags } from "../app/storeTypes.ts";

// Lazy store access — avoids circular module initialization.
// The store reference is set by initShadingDiag() called from a component
// that has already imported the store.
let _useStore: any = null;
let _storeUnsub: (() => void) | null = null;

/** Must be called once from a component that imports useStore (e.g. MannequinRenderer). */
export function initShadingDiag(store: any): void {
  if (_useStore === store) return;
  // If re-initialised with a different store (HMR, test teardown), drop the
  // previous subscription before wiring a new one so listeners don't stack.
  if (_storeUnsub) {
    _storeUnsub();
    _storeUnsub = null;
  }
  _useStore = store;
  // Now safe to sync with store state
  try {
    if (_useStore?.getState().debugEnabled) {
      shadingDiag.activate();
    }
    _storeUnsub = _useStore.subscribe((state: any, prev: any) => {
      if (state.debugEnabled !== prev.debugEnabled) {
        if (state.debugEnabled) shadingDiag.activate();
        else shadingDiag.deactivate();
      }
    });
  } catch {
    /* store not ready — will be retried */
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DiagCategory =
  | "NORMAL"
  | "MORPH"
  | "MATERIAL"
  | "BUFFER"
  | "COMPUTE";

export interface DiagEntry {
  category: DiagCategory;
  key: string;
  message: string;
  data?: Record<string, unknown>;
  level: "log" | "warn";
  count: number;
  firstTs: number;
  lastTs: number;
}

export interface NormalSampleResult {
  sampleCount: number;
  nanCount: number;
  zeroCount: number;
  nonUnitCount: number;
  minLength: number;
  maxLength: number;
  samples: Array<{ i: number; x: number; y: number; z: number; len: number }>;
}

export interface ShadingSnapshot {
  timestamp: string;
  morphInfluences: Record<string, number>;
  activeMorphCount: number;
  bonePoseCount: number;
  materialInfo: {
    type: string;
    uuid: string;
    hasNormalNode: boolean;
    hasPositionNode: boolean;
    hasNormalMap: boolean;
  } | null;
  bufferSizes: {
    positionCount: number;
    indexCount: number;
    morphTargetCount: number;
    influenceBufferLength: number;
  } | null;
  normalSamples: NormalSampleResult | null;
  shadingDebugFlags: ShadingDebugFlags;
  recentDiagEntries: DiagEntry[];
  frameStats: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEDUP_WINDOW_MS = 5_000;
const RECENT_BUFFER_SIZE = 200;
const ALL_CATEGORIES: DiagCategory[] = [
  "NORMAL",
  "MORPH",
  "MATERIAL",
  "BUFFER",
  "COMPUTE",
];

// ---------------------------------------------------------------------------
// Logger singleton
// ---------------------------------------------------------------------------

let _active = false;
const _enabled = new Set<DiagCategory>(ALL_CATEGORIES);
const _pending = new Map<string, DiagEntry>();
const _recent: DiagEntry[] = [];
let _flushTimer: ReturnType<typeof setInterval> | null = null;

function formatPrefix(cat: DiagCategory): string {
  return `[SHADING:${cat}]`;
}

function pushRecent(entry: DiagEntry): void {
  _recent.push(entry);
  if (_recent.length > RECENT_BUFFER_SIZE) _recent.shift();
}

function emitEntry(entry: DiagEntry): void {
  const prefix = formatPrefix(entry.category);
  const countStr = entry.count > 1 ? ` (x${entry.count})` : "";
  const fn = entry.level === "warn" ? console.warn : console.log;
  if (entry.data && Object.keys(entry.data).length > 0) {
    fn(`${prefix}${countStr} ${entry.message}`, entry.data);
  } else {
    fn(`${prefix}${countStr} ${entry.message}`);
  }
}

function flushPending(): void {
  for (const entry of _pending.values()) {
    if (entry.count > 1) {
      emitEntry(entry);
    }
    // count === 1 was already emitted on first occurrence
  }
  _pending.clear();
}

function startTimer(): void {
  if (_flushTimer) return;
  _flushTimer = setInterval(flushPending, DEDUP_WINDOW_MS);
}

function stopTimer(): void {
  if (_flushTimer) {
    clearInterval(_flushTimer);
    _flushTimer = null;
  }
}

function logImpl(
  level: "log" | "warn",
  category: DiagCategory,
  key: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  if (!_active || !_enabled.has(category)) return;

  const dedupKey = `${category}:${key}`;
  const existing = _pending.get(dedupKey);
  const now = performance.now();

  if (existing && now - existing.firstTs < DEDUP_WINDOW_MS) {
    existing.count++;
    existing.lastTs = now;
    // Update data to latest
    if (data) existing.data = data;
    return;
  }

  // New entry — emit immediately
  const entry: DiagEntry = {
    category,
    key,
    message,
    data,
    level,
    count: 1,
    firstTs: now,
    lastTs: now,
  };
  _pending.set(dedupKey, entry);
  pushRecent(entry);
  emitEntry(entry);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const shadingDiag = {
  log(
    category: DiagCategory,
    key: string,
    message: string,
    data?: Record<string, unknown>,
  ): void {
    logImpl("log", category, key, message, data);
  },

  warn(
    category: DiagCategory,
    key: string,
    message: string,
    data?: Record<string, unknown>,
  ): void {
    logImpl("warn", category, key, message, data);
  },

  enable(category: DiagCategory): void {
    _enabled.add(category);
  },

  disable(category: DiagCategory): void {
    _enabled.delete(category);
  },

  enableAll(): void {
    for (const c of ALL_CATEGORIES) _enabled.add(c);
  },

  disableAll(): void {
    _enabled.clear();
  },

  isEnabled(category: DiagCategory): boolean {
    return _enabled.has(category);
  },

  isActive(): boolean {
    return _active;
  },

  activate(): void {
    if (_active) return;
    _active = true;
    startTimer();
  },

  deactivate(): void {
    if (!_active) return;
    flushPending();
    _active = false;
    stopTimer();
  },

  flush: flushPending,

  getRecent(maxEntries = 50): DiagEntry[] {
    return _recent.slice(-maxEntries);
  },

  /** Capture full pipeline state for bug reports. */
  snapshot: null as unknown as (
    mesh?: THREE.SkinnedMesh | null,
    normalSBA?: { array: ArrayLike<number> } | null,
  ) => ShadingSnapshot,
};

// ---------------------------------------------------------------------------
// Snapshot
// ---------------------------------------------------------------------------

shadingDiag.snapshot = function snapshotImpl(
  mesh?: THREE.SkinnedMesh | null,
  normalSBA?: { array: ArrayLike<number> } | null,
): ShadingSnapshot {
  const flags = _useStore?.getState().shadingDebugFlags;
  const frameStats =
    (window.__poseref_frameStats as Record<string, unknown> | undefined) ??
    null;

  const morphInfluences: Record<string, number> = {};
  let activeMorphCount = 0;
  let bonePoseCount = 0;
  let materialInfo: ShadingSnapshot["materialInfo"] = null;
  let bufferSizes: ShadingSnapshot["bufferSizes"] = null;

  if (mesh) {
    // Morph influences
    const dict = mesh.morphTargetDictionary;
    const inf = mesh.morphTargetInfluences;
    if (dict && inf) {
      for (const [name, idx] of Object.entries(dict)) {
        const w = inf[idx] ?? 0;
        if (Math.abs(w) > 1e-6) {
          morphInfluences[name] = Math.round(w * 10000) / 10000;
          activeMorphCount++;
        }
      }
    }

    // Bone poses
    const bonePoses = _useStore?.getState().mannequinBonePoses;
    for (const [, poses] of bonePoses) {
      bonePoseCount += poses.size;
    }

    // Material info
    const mat = mesh.material as any;
    if (mat) {
      materialInfo = {
        type: mat.type ?? "unknown",
        uuid: mat.uuid ?? "",
        hasNormalNode: !!mat.normalNode,
        hasPositionNode: !!mat.positionNode,
        hasNormalMap: !!mat.normalMap,
      };
    }

    // Buffer sizes
    const geo = mesh.geometry;
    if (geo) {
      const posAttr = geo.getAttribute("position");
      const idxAttr = geo.index;
      const morphPos = geo.morphAttributes?.position as any[];
      bufferSizes = {
        positionCount: posAttr?.count ?? 0,
        indexCount: idxAttr?.count ?? 0,
        morphTargetCount: morphPos?.length ?? 0,
        influenceBufferLength: inf?.length ?? 0,
      };
    }
  }

  // Normal samples
  let normalSamples: NormalSampleResult | null = null;
  if (normalSBA?.array && normalSBA.array.length > 0) {
    normalSamples = sampleNormals(normalSBA.array, 64);
  }

  return {
    timestamp: new Date().toISOString(),
    morphInfluences,
    activeMorphCount,
    bonePoseCount,
    materialInfo,
    bufferSizes,
    normalSamples,
    shadingDebugFlags: { ...flags },
    recentDiagEntries: _recent.slice(-20),
    frameStats,
  };
};

// ---------------------------------------------------------------------------
// Normal sampling
// ---------------------------------------------------------------------------

export function sampleNormals(
  arr: ArrayLike<number>,
  sampleCount = 64,
): NormalSampleResult {
  const vertCount = arr.length / 4;
  if (vertCount === 0) {
    return {
      sampleCount: 0,
      nanCount: 0,
      zeroCount: 0,
      nonUnitCount: 0,
      minLength: 0,
      maxLength: 0,
      samples: [],
    };
  }

  const step = Math.max(1, Math.floor(vertCount / sampleCount));
  const samples: NormalSampleResult["samples"] = [];
  let nanCount = 0;
  let zeroCount = 0;
  let nonUnitCount = 0;
  let minLength = Infinity;
  let maxLength = -Infinity;

  for (let i = 0; i < vertCount; i += step) {
    const x = arr[i * 4];
    const y = arr[i * 4 + 1];
    const z = arr[i * 4 + 2];

    if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(z)) {
      nanCount++;
      samples.push({ i, x, y, z, len: NaN });
      continue;
    }

    const len = Math.sqrt(x * x + y * y + z * z);
    if (len < 1e-6) {
      zeroCount++;
    } else if (Math.abs(len - 1.0) > 0.01) {
      nonUnitCount++;
    }
    if (len < minLength) minLength = len;
    if (len > maxLength) maxLength = len;
    samples.push({
      i,
      x: +x.toFixed(4),
      y: +y.toFixed(4),
      z: +z.toFixed(4),
      len: +len.toFixed(4),
    });
  }

  return {
    sampleCount: samples.length,
    nanCount,
    zeroCount,
    nonUnitCount,
    minLength: minLength === Infinity ? 0 : +minLength.toFixed(4),
    maxLength: maxLength === -Infinity ? 0 : +maxLength.toFixed(4),
    samples,
  };
}

// ---------------------------------------------------------------------------
// Validation functions
// ---------------------------------------------------------------------------

/** Validate GPU pipeline state after creation. */
export function validateGpuPipeline(
  mesh: THREE.SkinnedMesh,
  influenceData: Float32Array,
): void {
  if (!_active) return;

  const geo = mesh.geometry;
  const posAttr = geo.getAttribute("position");
  const vertCount = posAttr?.count ?? 0;

  // Morph count consistency — note: in render mode, morphAttributes are
  // intentionally stripped from the geometry (to prevent Three.js from
  // compiling its own morph handling).  The GPU pipeline receives morph
  // data explicitly, so influenceData.length is the authoritative count.
  const morphPos = geo.morphAttributes?.position as any[];
  const geoMorphCount = morphPos?.length ?? 0;
  const infLen = influenceData.length;
  if (geoMorphCount > 0 && infLen < geoMorphCount) {
    shadingDiag.warn(
      "COMPUTE",
      "pipeline-morph-mismatch",
      `influenceData.length (${infLen}) < geo morphCount (${geoMorphCount})`,
      { infLen, geoMorphCount },
    );
  }

  // Skeleton integrity
  const skeleton = mesh.skeleton;
  if (!skeleton || skeleton.bones.length === 0) {
    shadingDiag.warn(
      "COMPUTE",
      "pipeline-no-skeleton",
      "Skeleton missing or has no bones",
    );
  } else if (!skeleton.boneMatrices) {
    shadingDiag.warn(
      "COMPUTE",
      "pipeline-no-bone-matrices",
      "skeleton.boneMatrices is null",
    );
  }

  // Skin attributes
  const skinIdx = geo.getAttribute("skinIndex");
  const skinWgt = geo.getAttribute("skinWeight");
  if (!skinIdx || !skinWgt) {
    shadingDiag.warn(
      "BUFFER",
      "pipeline-missing-skin-attrs",
      `skinIndex: ${!!skinIdx}, skinWeight: ${!!skinWgt}`,
    );
  } else if (skinIdx.count !== vertCount || skinWgt.count !== vertCount) {
    shadingDiag.warn(
      "BUFFER",
      "pipeline-skin-count-mismatch",
      `skinIndex.count=${skinIdx.count}, skinWeight.count=${skinWgt.count}, vertCount=${vertCount}`,
    );
  }

  shadingDiag.log(
    "COMPUTE",
    "pipeline-created",
    `GPU normal pipeline created: ${vertCount} verts, ${infLen} morphs (${geoMorphCount} on geo), ${skeleton?.bones.length ?? 0} bones`,
  );
}

/** Validate state before GPU compute dispatch. Call every ~60 frames. */
export function validatePreDispatch(
  mesh: THREE.SkinnedMesh,
  influenceData: Float32Array,
): void {
  if (!_active) return;

  // Check for NaN in influence data
  let nanCount = 0;
  let outOfRange = 0;
  for (let i = 0; i < influenceData.length; i++) {
    if (Number.isNaN(influenceData[i])) nanCount++;
    else if (Math.abs(influenceData[i]) > 2.0) outOfRange++;
  }
  if (nanCount > 0) {
    shadingDiag.warn(
      "MORPH",
      "influence-nan",
      `${nanCount} NaN values in influenceData`,
      { nanCount, length: influenceData.length },
    );
  }
  if (outOfRange > 0) {
    shadingDiag.warn(
      "MORPH",
      "influence-range",
      `${outOfRange} influence values outside [-2, 2]`,
      { outOfRange },
    );
  }

  // Sample bone matrices for NaN
  const bm = mesh.skeleton?.boneMatrices;
  if (bm) {
    let boneNan = false;
    // Check first and last bone (16 floats each)
    for (let j = 0; j < 16 && j < bm.length; j++) {
      if (Number.isNaN(bm[j])) {
        boneNan = true;
        break;
      }
    }
    if (!boneNan && bm.length > 16) {
      const lastStart = bm.length - 16;
      for (let j = lastStart; j < bm.length; j++) {
        if (Number.isNaN(bm[j])) {
          boneNan = true;
          break;
        }
      }
    }
    if (boneNan) {
      shadingDiag.warn(
        "COMPUTE",
        "bone-matrix-nan",
        "NaN detected in bone matrices",
      );
    }
  }
}

/** Validate material after TSL node patching. */
export function validateMaterialPatch(
  material: THREE.Material,
  geometry: THREE.BufferGeometry,
  hasGpuPipeline: boolean,
): void {
  if (!_active) return;

  const mat = material as any;

  if (hasGpuPipeline && !mat.normalNode) {
    shadingDiag.warn(
      "MATERIAL",
      "missing-normalNode",
      `Material ${mat.type} has GPU pipeline but no normalNode`,
    );
  }

  // Required attributes
  const required = ["position", "normal", "skinIndex", "skinWeight"] as const;
  const missing = required.filter((name) => !geometry.getAttribute(name));
  if (missing.length > 0) {
    shadingDiag.warn(
      "BUFFER",
      "missing-geometry-attrs",
      `Missing geometry attributes: ${missing.join(", ")}`,
    );
  }

  shadingDiag.log(
    "MATERIAL",
    "patch-applied",
    `Material patched: ${mat.type}, normalNode=${!!mat.normalNode}, positionNode=${!!mat.positionNode}`,
    { uuid: mat.uuid, type: mat.type },
  );
}

// Expose on window for console access
window.__shadingDiag = shadingDiag;

// Catch unhandled WebGPU promise rejections to surface useful error details.
// Standard JS Error instances (TypeError, RangeError, etc.) are excluded —
// they come from non-GPU sources (e.g. a failed worker module load) and would
// produce misleading [SHADING:COMPUTE] warnings if captured here.
// Gated on a window-level flag so HMR reloads of this module don't stack
// duplicate listeners on the shared window.
const UNHANDLED_REJECTION_FLAG = "__shadingDiagUnhandledRejectionAttached";
if (!(window as any)[UNHANDLED_REJECTION_FLAG]) {
  (window as any)[UNHANDLED_REJECTION_FLAG] = true;
  window.addEventListener("unhandledrejection", (event) => {
    if (!_active) return;
    const reason = event.reason;
    if (reason instanceof Error) return;
    // WebGPU errors surface as Event objects or GPUValidationError/GPUOutOfMemoryError/GPUInternalError
    if (
      reason instanceof Event ||
      (reason && typeof reason === "object" && "message" in reason)
    ) {
      const msg =
        (reason as any).message ?? (reason as Event).type ?? String(reason);
      shadingDiag.warn(
        "COMPUTE",
        "unhandled-gpu-rejection",
        `Unhandled GPU rejection: ${msg}`,
        { type: (reason as any).type, message: (reason as any).message },
      );
    }
  });
}
